import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import {
  Bot,
  Boxes,
  FileText,
  Home,
  Plug,
  Scale,
  Settings,
  Sparkles,
  Users,
} from "lucide-react";
import {
  AskAI,
  AttentionRow,
  Button,
  CounterTiles,
  SetupChecklist,
  Sidebar,
  StageStepper,
  Table,
  TableRow,
  Toast,
  TopBar,
  WaitingCard,
  type DsNavGroup,
  type StageStep,
  type WaitingEntry,
  type WaitingTask,
} from "../components/ds";
import { sortStageRfqs } from "../components/ds/model";
import { dsCard } from "../components/ds/styles";
import { loadWebSession } from "../lib/authSession";
import { greetingName } from "../lib/greeting";
import { useLanguage } from "../lib/i18n";
import { applyTheme, loadTheme } from "../lib/theme";
import { cx } from "../lib/cx";
import { pushRoute } from "../routes";

/** Reference stage tracks: one grid, so every stepper shares an x. */
const STAGE_COLUMNS = [
  "minmax(180px,1.5fr)",
  "minmax(220px,2fr)",
  "120px",
  "auto",
] as const;

function formatDue(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale === "tr" ? "tr-TR" : "en-GB", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  }).format(new Date(iso));
}

/** Opens the RFQ list. The card button and any later caller share this. */
function openRfqList(event: { preventDefault(): void }) {
  event.preventDefault();
  pushRoute("rfq");
  window.dispatchEvent(new PopStateEvent("popstate"));
}

const iconClass = "size-[17px]";

function initialsOf(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

/** Equal columns. Stretch so the shorter card meets its partner's height. */
const pairClass = "grid w-full grid-cols-1 items-stretch gap-ds-cards min-[1181px]:grid-cols-2";

const SETUP_DONE = 1;
const SETUP_TOTAL = 3;
const SETUP_TITLE_ID = "ds-setup-title";
const PARTS_TITLE_ID = "ds-parts-title";

const WIDE_QUERY = "(min-width: 1181px)";
/** 780-tall shells. Fonts stay; padding, gap and rows come in a step. */
const MID_QUERY = "(min-width: 1181px) and (max-height: 820px)";
/** 720-tall shells. One step tighter than the 780 tier. */
const SHORT_QUERY = "(min-width: 1181px) and (max-height: 740px)";

const MID_ROW_VARS = {
  "--spacing-ds-row-setup": "44px",
  "--spacing-ds-row-table": "50px",
} as CSSProperties;

const SHORT_ROW_VARS = {
  "--spacing-ds-row-setup": "40px",
  "--spacing-ds-row-table": "46px",
} as CSSProperties;

function useMedia(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window.matchMedia === "function" ? window.matchMedia(query).matches : false,
  );
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(query);
    const sync = () => setMatches(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, [query]);
  return matches;
}

/**
 * Dev / demo gallery. Every string is i18n, and every row is tagged example.
 * Not linked from the product nav.
 */
export function DesignGalleryPage() {
  const { t, locale } = useLanguage();
  const wide = useMedia(WIDE_QUERY);
  const mid = useMedia(MID_QUERY);
  const short = useMedia(SHORT_QUERY);
  const [active, setActive] = useState("home");
  const [search, setSearch] = useState("");
  const [ask, setAsk] = useState("");
  const [toast, setToast] = useState<string | null>(null);
  const [overlay, setOverlay] = useState<"setup" | "parts" | null>(null);

  useEffect(() => {
    if (!toast) return;
    const id = window.setTimeout(() => setToast(null), 2400);
    return () => window.clearTimeout(id);
  }, [toast]);

  // The gallery is one viewport and always light. Lock the document so product
  // `min-height: 100vh` cannot grow a page scrollbar, and put the product theme
  // back on the way out. There is no gallery theme toggle.
  useLayoutEffect(() => {
    applyTheme("light");
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById("root");
    const htmlPrev = { overflow: html.style.overflow, height: html.style.height, minHeight: html.style.minHeight };
    const bodyPrev = { overflow: body.style.overflow, height: body.style.height, minHeight: body.style.minHeight };
    const rootPrev = root
      ? { overflow: root.style.overflow, height: root.style.height, minHeight: root.style.minHeight }
      : null;
    html.style.overflow = "hidden";
    html.style.height = "100dvh";
    html.style.minHeight = "0px";
    body.style.overflow = "hidden";
    body.style.height = "100%";
    body.style.minHeight = "0px";
    if (root) {
      root.style.overflow = "hidden";
      root.style.height = "100%";
      root.style.minHeight = "0px";
    }
    return () => {
      html.style.overflow = htmlPrev.overflow;
      html.style.height = htmlPrev.height;
      html.style.minHeight = htmlPrev.minHeight;
      body.style.overflow = bodyPrev.overflow;
      body.style.height = bodyPrev.height;
      body.style.minHeight = bodyPrev.minHeight;
      if (root && rootPrev) {
        root.style.overflow = rootPrev.overflow;
        root.style.height = rootPrev.height;
        root.style.minHeight = rootPrev.minHeight;
      }
      applyTheme(loadTheme());
    };
  }, []);

  const date = useMemo(
    () =>
      new Intl.DateTimeFormat(locale === "tr" ? "tr-TR" : "en-GB", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      }).format(new Date()),
    [locale],
  );

  const steps: StageStep[] = [
    { id: "files", label: t("ds.stage.files") },
    { id: "stock", label: t("ds.stage.stock") },
    { id: "bom", label: t("ds.stage.bom") },
    { id: "params", label: t("ds.stage.params") },
    { id: "cost", label: t("ds.stage.cost") },
    { id: "quote", label: t("ds.stage.quote") },
    { id: "outcome", label: t("ds.stage.outcome") },
  ];

  const groups: DsNavGroup[] = [
    {
      id: "workspace",
      label: t("sidebar.workspace"),
      items: [
        { id: "home", label: t("palette.home"), icon: <Home className={iconClass} strokeWidth={1.5} />, active: active === "home", onSelect: () => setActive("home") },
        { id: "documents", label: t("sidebar.documents"), icon: <FileText className={iconClass} strokeWidth={1.5} />, active: active === "documents", onSelect: () => setActive("documents") },
        { id: "rfq", label: t("palette.rfq"), icon: <Scale className={iconClass} strokeWidth={1.5} />, active: active === "rfq", onSelect: () => setActive("rfq") },
        { id: "customers", label: t("palette.customers"), icon: <Users className={iconClass} strokeWidth={1.5} />, active: active === "customers", onSelect: () => setActive("customers") },
      ],
    },
    {
      id: "ai",
      label: t("sidebar.group.ai"),
      items: [
        { id: "ai", label: t("palette.aiShort"), icon: <Sparkles className={iconClass} strokeWidth={1.5} />, active: active === "ai", onSelect: () => setActive("ai") },
        { id: "agents", label: t("sidebar.agents"), icon: <Bot className={iconClass} strokeWidth={1.5} />, badge: t("sidebar.soon"), disabled: true },
      ],
    },
    {
      id: "data",
      label: t("sidebar.group.data"),
      items: [
        { id: "stock", label: t("sidebar.stock"), icon: <Boxes className={iconClass} strokeWidth={1.5} />, active: active === "stock", onSelect: () => setActive("stock") },
        { id: "integrations", label: t("sidebar.integrations"), icon: <Plug className={iconClass} strokeWidth={1.5} />, badge: t("sidebar.comingSoon"), disabled: true },
      ],
    },
  ];

  const signedInName = greetingName(loadWebSession() ?? {});
  const displayName = signedInName || t("ds.gallery.userName");

  const attention = [
    { id: "confidence", name: t("ds.gallery.exampleRfq"), meta: t("ds.gallery.partMeta"), reason: t("ds.gallery.partReason"), tone: "coral" as const },
    { id: "dfm", name: t("ds.gallery.partDfmName"), meta: t("ds.gallery.partMeta"), reason: t("ds.gallery.partDfm"), tone: "amber" as const },
    { id: "stock", name: t("ds.gallery.partStockName"), meta: t("ds.gallery.partMeta"), reason: t("ds.gallery.partStock"), tone: "coral" as const },
  ];

  const tasks: WaitingTask[] = [
    { id: "price", category: "price", title: t("ds.alert.cat.price"), subtitle: t("ds.gallery.taskPriceMeta"), actionLabel: t("ds.gallery.approve"), onAction: () => setToast(t("ds.gallery.toast")) },
    { id: "setup", category: "setup", title: t("ds.alert.cat.setup"), subtitle: t("ds.gallery.taskSetupMeta"), actionLabel: t("ds.gallery.addMachine"), onAction: () => setToast(t("ds.gallery.toast")) },
    { id: "stock", category: "stock", title: t("ds.alert.cat.stock"), subtitle: t("ds.gallery.taskStockMeta"), actionLabel: t("ds.gallery.match"), onAction: () => setToast(t("ds.gallery.toast")) },
    { id: "dfm", category: "dfm", title: t("ds.alert.cat.dfm"), subtitle: t("ds.gallery.taskDfmMeta"), actionLabel: t("ds.gallery.open"), onAction: () => setToast(t("ds.gallery.toast")) },
    { id: "bom", category: "bom", title: t("ds.alert.cat.bom"), subtitle: t("ds.gallery.taskBomMeta"), actionLabel: t("ds.gallery.approve"), onAction: () => setToast(t("ds.gallery.toast")) },
    { id: "confidence", category: "confidence", title: t("ds.alert.cat.confidence"), subtitle: t("ds.gallery.taskConfMeta"), actionLabel: t("ds.gallery.open"), onAction: () => setToast(t("ds.gallery.toast")) },
  ];

  // Two urgent tasks plus the setup and parts entries fit 720px. A third task does not.
  const entries: WaitingEntry[] = [
    {
      id: "setup",
      title: t("ds.gallery.setupTitle"),
      subtitle: t("ds.setup.progress", { done: SETUP_DONE, total: SETUP_TOTAL }),
      actionLabel: t("ds.gallery.open"),
      tone: "amber",
      onOpen: () => setOverlay("setup"),
    },
    {
      id: "parts",
      title: t("ds.gallery.attentionTitle"),
      subtitle: t("ds.gallery.partsCount", { count: attention.length }),
      actionLabel: t("ds.gallery.open"),
      tone: "coral",
      onOpen: () => setOverlay("parts"),
    },
  ];

  function exampleName(name: string, id: string) {
    return (
      <span className="block min-w-0">
        <b className="block whitespace-nowrap text-ds-item font-semibold text-ds-ink">
          {name}
          <span className="ml-1.5 inline-block rounded-ds-pill border border-solid border-ds-hair px-1.5 py-px align-middle text-ds-badge-sm font-normal text-ds-ink-3">
            {t("ds.attention.example")}
          </span>
        </b>
        <small className="mt-0.5 block whitespace-nowrap text-ds-label font-normal text-ds-ink-3">{id}</small>
      </span>
    );
  }

  return (
    <div
      data-testid="ds-gallery"
      className={cx(
        "h-dvh max-h-dvh overflow-hidden bg-ds-page font-ds text-ds-ink",
        wide && !mid && "[&_section.rounded-ds-card]:!py-4",
        mid && !short && "[&_section.rounded-ds-card]:!px-4 [&_section.rounded-ds-card]:!py-3",
        short && "[&_section.rounded-ds-card]:!px-4 [&_section.rounded-ds-card]:!py-2.5 [&_.mb-ds-head]:!mb-1.5",
      )}
      style={short ? SHORT_ROW_VARS : mid ? MID_ROW_VARS : undefined}
    >
      <div className="flex h-full min-h-0 items-stretch overflow-hidden max-[1180px]:flex-col max-[1180px]:overflow-y-auto">
        <Sidebar
          wordmark={t("ds.gallery.brand")}
          wordmarkAccent={t("ds.gallery.brandAccent")}
          mark={t("ds.gallery.mark")}
          groups={groups}
          settings={{
            id: "settings",
            label: t("sidebar.settings"),
            icon: <Settings className={iconClass} strokeWidth={1.5} />,
            active: active === "settings",
            onSelect: () => setActive("settings"),
          }}
          user={{
            name: displayName,
            detail: t("ds.gallery.userEmail"),
            initials: signedInName ? initialsOf(signedInName) : t("ds.gallery.initials"),
          }}
        />
        <main
          data-testid="ds-gallery-main"
          className={cx(
            "flex min-h-0 min-w-0 flex-1 flex-col px-ds-page-x py-ds-page-y",
            wide ? "h-full overflow-hidden" : "h-auto flex-none overflow-visible",
            mid && !short && "!px-4 !py-2.5",
            short && "!px-3.5 !py-2",
          )}
        >
          <div
            className={cx(
              "flex w-full flex-col gap-ds-cards",
              wide && "h-full min-h-0",
              wide && !mid && "!gap-3",
              mid && !short && "!gap-2",
              short && "!gap-1.5",
            )}
          >
            <div className="shrink-0">
            <TopBar
              searchValue={search}
              onSearchChange={setSearch}
              searchPlaceholder={t("ds.gallery.searchPlaceholder")}
              onNotify={() => setToast(t("ds.gallery.toast"))}
              primaryLabel={t("ds.gallery.primary")}
              onPrimary={() => setToast(t("ds.gallery.toast"))}
            />
            </div>
            <div className={pairClass}>
              <WaitingCard
                title={t("ds.alert.title")}
                welcome={t("ds.gallery.welcome", { name: displayName })}
                date={date}
                tasks={tasks}
                maxRows={2}
                entries={entries}
              />
              <CounterTiles
                title={t("ds.gallery.dueTitle")}
                meta={t("ds.gallery.dueMeta")}
                tiles={[
                  { id: "overdue", value: "2", label: t("ds.gallery.overdue"), tone: "coral" },
                  { id: "today", value: "1", label: t("ds.gallery.dueToday") },
                  { id: "week", value: "4", label: t("ds.gallery.thisWeek") },
                ]}
              />
            </div>
            <AskAI
                title={t("ds.gallery.askTitle")}
                meta={t("ds.gallery.askMeta")}
                placeholder={t("ds.gallery.askPlaceholder")}
                suggestions={[
                  { id: "stock", label: t("ds.gallery.suggest1") },
                  { id: "block", label: t("ds.gallery.suggest2") },
                  { id: "labour", label: t("ds.gallery.suggest3") },
                ]}
                value={ask}
                onChange={setAsk}
                onSubmit={() => {
                  setAsk("");
                  setToast(t("ds.gallery.toast"));
                }}
            />
            <section
              data-testid="ds-rfqs"
              className={cx(dsCard, "flex h-full flex-col")}
              aria-label={t("ds.gallery.rfqTitle")}
            >
              <header className="mb-ds-head flex shrink-0 items-baseline justify-between gap-2.5">
                <h2 className="m-0 font-ds text-ds-card font-semibold text-ds-ink">{t("ds.gallery.rfqTitle")}</h2>
                <Button variant="link" onClick={openRfqList}>
                  {t("ds.gallery.allRfqs")}
                </Button>
              </header>
              <Table
                maxRows={3}
                columns={[
                  { id: "rfq", header: t("ds.gallery.colRfq"), width: STAGE_COLUMNS[0] },
                  { id: "stage", header: t("ds.gallery.colStage"), width: STAGE_COLUMNS[1] },
                  { id: "due", header: t("ds.gallery.colDue"), width: STAGE_COLUMNS[2] },
                  { id: "action", header: "", width: STAGE_COLUMNS[3], align: "end" },
                ]}
              >
                {sortStageRfqs([
                  { id: "example-5", name: t("ds.gallery.exampleRfq5"), rfqNo: t("ds.gallery.exampleRfq5Id"), due_at: null, updated_at: "2026-10-03T12:00:00Z", stageIndex: 0 },
                  { id: "example-4", name: t("ds.gallery.exampleRfq4"), rfqNo: t("ds.gallery.exampleRfq4Id"), due_at: "2026-11-02T00:00:00Z", updated_at: "2026-10-03T08:00:00Z", stageIndex: 2 },
                  { id: "example-2", name: t("ds.gallery.exampleRfq2"), rfqNo: t("ds.gallery.exampleRfq2Id"), due_at: "2026-10-08T00:00:00Z", updated_at: "2026-09-02T00:00:00Z", stageIndex: 1 },
                  { id: "example-3", name: t("ds.gallery.exampleRfq3"), rfqNo: t("ds.gallery.exampleRfq3Id"), due_at: "2026-10-15T00:00:00Z", updated_at: "2026-09-03T00:00:00Z", stageIndex: 5 },
                  { id: "example-1", name: t("ds.gallery.exampleRfq"), rfqNo: t("ds.gallery.exampleRfqId"), due_at: "2026-10-04T00:00:00Z", updated_at: "2026-09-01T00:00:00Z", stageIndex: 3 },
                ]).map((rfq) => (
                  <TableRow
                    key={rfq.id}
                    onOpen={() => setToast(t("ds.gallery.toast"))}
                    cells={[
                      exampleName(rfq.name, rfq.rfqNo),
                      <StageStepper key="stage" steps={steps} currentIndex={rfq.stageIndex} />,
                      rfq.due_at ? (
                        <span key="due" className="text-ds-meta font-normal text-ds-ink-3">
                          {formatDue(rfq.due_at, locale)}
                        </span>
                      ) : null,
                    ]}
                  />
                ))}
              </Table>
            </section>
          </div>
        </main>
      </div>
      {overlay ? (
        <GalleryDialog
          titleId={overlay === "setup" ? SETUP_TITLE_ID : PARTS_TITLE_ID}
          closeLabel={t("common.close")}
          onClose={() => setOverlay(null)}
        >
          {overlay === "setup" ? (
            <SetupChecklist
              title={t("ds.gallery.setupTitle")}
              titleId={SETUP_TITLE_ID}
              done={SETUP_DONE}
              total={SETUP_TOTAL}
              headerAction={{ label: t("sidebar.settings"), onClick: () => setToast(t("ds.gallery.toast")) }}
              steps={[
                { id: "machines", state: "next", name: t("ds.gallery.stepMachines"), description: t("ds.gallery.stepMachinesBody"), actionLabel: t("ds.gallery.add"), onAction: () => setToast(t("ds.gallery.toast")) },
                { id: "stock", state: "done", name: t("ds.gallery.stepStock"), description: t("ds.gallery.stepStockBody"), actionLabel: t("ds.gallery.import"), onAction: () => setToast(t("ds.gallery.toast")) },
                { id: "pricing", state: "later", name: t("ds.gallery.stepPricing"), description: t("ds.gallery.stepPricingBody"), actionLabel: t("ds.gallery.setupAction"), onAction: () => setToast(t("ds.gallery.toast")) },
              ]}
            />
          ) : (
            <section
              data-testid="ds-attention-card"
              className={cx(dsCard, "flex h-full flex-col")}
              aria-label={t("ds.gallery.attentionTitle")}
            >
              <header className="mb-ds-head shrink-0">
                <h2 id={PARTS_TITLE_ID} className="m-0 font-ds text-ds-card font-semibold text-ds-ink">
                  {t("ds.gallery.attentionTitle")}
                </h2>
              </header>
              <div className="flex flex-col">
                {attention.map((part) => (
                  <AttentionRow
                    key={part.id}
                    example
                    name={part.name}
                    meta={part.meta}
                    reason={part.reason}
                    tone={part.tone}
                    actionLabel={t("ds.gallery.review")}
                    onAction={() => setToast(t("ds.gallery.toast"))}
                  />
                ))}
              </div>
            </section>
          )}
        </GalleryDialog>
      ) : null}
      <Toast message={toast ?? ""} open={toast !== null} />
    </div>
  );
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Centered card over a dimmed, blurred page. Esc, backdrop, and Close dismiss it. */
function GalleryDialog({
  titleId,
  closeLabel,
  onClose,
  children,
}: {
  titleId: string;
  closeLabel: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const panel = panelRef.current;
    panel?.querySelector<HTMLElement>("[data-testid='ds-dialog-close']")?.focus();

    function items(): HTMLElement[] {
      if (!panel) return [];
      return Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex !== -1,
      );
    }

    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab" || !panel) return;
      const list = items();
      if (list.length === 0) return;
      const first = list[0];
      const last = list[list.length - 1];
      const active = document.activeElement;
      const inside = active instanceof Node && panel.contains(active);
      if (event.shiftKey && (active === first || !inside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (trigger?.isConnected) trigger.focus();
    };
  }, []);

  return (
    <div
      data-testid="ds-dialog-scrim"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-ds-on-amber/40 p-6 backdrop-blur-ds"
      onClick={(event) => {
        if (event.target === event.currentTarget) onCloseRef.current();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="ds-dialog"
        className="flex w-full max-w-[572px] flex-col gap-2"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="flex justify-end">
          <Button variant="secondary" data-testid="ds-dialog-close" onClick={() => onCloseRef.current()}>
            {closeLabel}
          </Button>
        </div>
        {children}
      </div>
    </div>
  );
}
